import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import tradingSalesInvoiceService from "../services/tradingSalesInvoiceService";

const getResponseData = (response) =>
  response?.data?.data ?? response?.data ?? {};

const getErrorMessage = (err) =>
  err?.response?.data?.message ||
  err?.response?.data?.error ||
  err?.message ||
  "Unable to process Sales Invoice.";

const normalizeStatus = (value) =>
  String(value || "DRAFT").trim().toUpperCase();

const formatAmount = (value) =>
  Number(value || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const formatQuantity = (value) =>
  Number(value || 0).toLocaleString("en-IN", {
    maximumFractionDigits: 6,
  });

const formatDate = (value) => {
  if (!value) return "—";
  const raw = String(value).slice(0, 10);
  const parts = raw.split("-");
  if (parts.length !== 3) return raw;
  return new Date(`${raw}T00:00:00`).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const formatDateTime = (value) => {
  if (!value) return "—";
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime())
    ? String(value)
    : parsed.toLocaleString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
};

const statusStyles = {
  DRAFT: { background: "#eeeaff", color: "#5948c7" },
  PENDING_APPROVAL: { background: "#fff4d8", color: "#956000" },
  APPROVED: { background: "#e5efff", color: "#2056a5" },
  REJECTED: { background: "#fee8e7", color: "#b42318" },
  POSTED: { background: "#dcfce7", color: "#166534" },
  CANCELLED: { background: "#eef0f4", color: "#667085" },
};

const styles = {
  page: {
    minHeight: "100vh",
    padding: "24px 28px",
    background: "#f5f7fb",
    color: "#1f2937",
    fontFamily: "Inter, Arial, sans-serif",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    flexWrap: "wrap",
    gap: 14,
    marginBottom: 18,
  },
  title: { margin: 0, color: "#172554", fontSize: 24, fontWeight: 800 },
  subtitle: { margin: "5px 0 0", color: "#667085", fontSize: 13 },
  buttonRow: { display: "flex", flexWrap: "wrap", gap: 8 },
  button: {
    border: "1px solid #d8deea",
    background: "#fff",
    color: "#344054",
    borderRadius: 9,
    padding: "9px 13px",
    fontSize: 12,
    fontWeight: 700,
    cursor: "pointer",
  },
  primaryButton: {
    border: "1px solid #5145cd",
    background: "#5145cd",
    color: "#fff",
    borderRadius: 9,
    padding: "9px 14px",
    fontSize: 12,
    fontWeight: 750,
    cursor: "pointer",
  },
  dangerButton: {
    border: "1px solid #f1c5c3",
    background: "#fff",
    color: "#b42318",
    borderRadius: 9,
    padding: "9px 13px",
    fontSize: 12,
    fontWeight: 700,
    cursor: "pointer",
  },
  card: {
    background: "#fff",
    border: "1px solid #e7eaf1",
    borderRadius: 15,
    boxShadow: "0 3px 12px rgba(20,35,70,.035)",
  },
  hero: { padding: 20, marginBottom: 16 },
  heroTop: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    flexWrap: "wrap",
    gap: 18,
  },
  invoiceNo: {
    fontSize: 21,
    fontWeight: 800,
    color: "#172554",
    margin: 0,
  },
  customerName: {
    fontSize: 15,
    fontWeight: 750,
    color: "#26324b",
    margin: "0 0 5px",
  },
  muted: { color: "#7b8497", fontSize: 12 },
  status: {
    display: "inline-flex",
    alignItems: "center",
    borderRadius: 20,
    padding: "5px 10px",
    fontSize: 11,
    fontWeight: 800,
    letterSpacing: ".02em",
    whiteSpace: "nowrap",
  },
  amountBlock: { textAlign: "right", minWidth: 180 },
  grandAmount: {
    fontSize: 27,
    fontWeight: 850,
    color: "#172554",
    margin: "5px 0 0",
  },
  metricGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit,minmax(150px,1fr))",
    gap: 10,
    marginTop: 18,
  },
  metric: {
    background: "#f7f8fc",
    border: "1px solid #edf0f5",
    borderRadius: 11,
    padding: "11px 13px",
  },
  metricLabel: { color: "#7a8498", fontSize: 11, marginBottom: 5 },
  metricValue: { color: "#27334b", fontSize: 15, fontWeight: 800 },
  lifecycle: {
    marginTop: 18,
    paddingTop: 15,
    borderTop: "1px solid #edf0f5",
  },
  steps: {
    display: "grid",
    gridTemplateColumns: "repeat(4,minmax(0,1fr))",
    gap: 8,
    marginTop: 10,
  },
  stepTrack: {
    height: 5,
    borderRadius: 8,
    background: "#e9edf4",
    overflow: "hidden",
  },
  stepFill: {
    height: "100%",
    borderRadius: 8,
    background: "#16a36a",
  },
  stepLabel: { marginTop: 6, fontSize: 10, color: "#788196" },
  contentGrid: {
    display: "grid",
    gridTemplateColumns: "minmax(0,1fr) 310px",
    gap: 16,
    alignItems: "start",
  },
  mainCard: { padding: 18, minWidth: 0 },
  sideCard: { padding: 18, position: "sticky", top: 16 },
  sectionTitle: {
    margin: 0,
    color: "#25314a",
    fontSize: 14,
    fontWeight: 800,
  },
  tabs: {
    display: "flex",
    flexWrap: "wrap",
    gap: 5,
    margin: "15px 0 0",
    borderBottom: "1px solid #e9edf3",
    paddingBottom: 9,
  },
  tab: {
    border: 0,
    borderRadius: 8,
    padding: "8px 10px",
    background: "transparent",
    color: "#667085",
    fontSize: 11,
    fontWeight: 700,
    cursor: "pointer",
  },
  activeTab: { background: "#eeecff", color: "#5145cd" },
  tableWrap: { width: "100%", overflowX: "auto", marginTop: 13 },
  table: {
    width: "100%",
    minWidth: 650,
    borderCollapse: "collapse",
    fontSize: 11,
  },
  th: {
    textAlign: "left",
    color: "#778197",
    fontWeight: 750,
    padding: "10px 9px",
    background: "#f8f9fc",
    borderBottom: "1px solid #e9edf3",
    whiteSpace: "nowrap",
  },
  td: {
    padding: "11px 9px",
    borderBottom: "1px solid #edf0f5",
    color: "#344054",
    verticalAlign: "top",
  },
  right: { textAlign: "right", whiteSpace: "nowrap" },
  infoGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit,minmax(180px,1fr))",
    gap: 14,
    marginTop: 16,
  },
  infoLabel: { color: "#8891a3", fontSize: 10, marginBottom: 4 },
  infoValue: {
    color: "#344054",
    fontSize: 12,
    fontWeight: 650,
    whiteSpace: "pre-wrap",
    overflowWrap: "anywhere",
  },
  summaryRow: {
    display: "flex",
    justifyContent: "space-between",
    gap: 12,
    padding: "8px 0",
    color: "#667085",
    fontSize: 12,
  },
  summaryStrong: {
    color: "#344054",
    fontWeight: 750,
    whiteSpace: "nowrap",
  },
  totalRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 10,
    borderTop: "1px solid #e9edf3",
    marginTop: 8,
    paddingTop: 14,
  },
  alert: {
    borderRadius: 10,
    padding: "11px 13px",
    marginBottom: 14,
    fontSize: 12,
    background: "#fff1f0",
    color: "#b42318",
    border: "1px solid #ffd6d2",
  },
  note: {
    marginTop: 13,
    borderRadius: 10,
    padding: 11,
    background: "#f5f7fb",
    color: "#667085",
    fontSize: 11,
    lineHeight: 1.5,
  },
  timelineItem: {
    display: "grid",
    gridTemplateColumns: "12px minmax(0,1fr)",
    gap: 10,
    padding: "11px 0",
    borderBottom: "1px solid #edf0f5",
  },
  dot: {
    width: 9,
    height: 9,
    borderRadius: "50%",
    background: "#6254d9",
    marginTop: 4,
  },
  empty: {
    textAlign: "center",
    color: "#8992a4",
    padding: "32px 10px",
    fontSize: 12,
  },
};

const Info = ({ label, value }) => (
  <div>
    <div style={styles.infoLabel}>{label}</div>
    <div style={styles.infoValue}>{value || "—"}</div>
  </div>
);

const StatusBadge = ({ status }) => {
  const normalized = normalizeStatus(status);
  const palette = statusStyles[normalized] || statusStyles.DRAFT;

  return (
    <span style={{ ...styles.status, ...palette }}>
      {normalized.replaceAll("_", " ")}
    </span>
  );
};

export default function TradingSalesInvoiceViewPage() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [invoice, setInvoice] = useState(null);
  const [audit, setAudit] = useState([]);
  const [activeTab, setActiveTab] = useState("items");
  const [loading, setLoading] = useState(true);
  const [auditLoading, setAuditLoading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const loadInvoice = useCallback(async () => {
    if (!id) {
      setError("Sales Invoice ID is missing.");
      setLoading(false);
      return;
    }

    setLoading(true);
    setError("");

    try {
      const response =
        await tradingSalesInvoiceService.getSalesInvoiceDetail(id);

      const body = getResponseData(response);
      const detail = body.invoice || body.data || body;

      if (!detail?.id) {
        throw new Error("Sales Invoice details were not returned.");
      }

      setInvoice(detail);
    } catch (err) {
      setInvoice(null);
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [id]);

  const loadAudit = useCallback(async () => {
    if (!id) return;

    setAuditLoading(true);

    try {
      const response =
        await tradingSalesInvoiceService.getSalesInvoiceAuditHistory(id);

      const body = getResponseData(response);
      setAudit(Array.isArray(body.history) ? body.history : []);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setAuditLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadInvoice();
  }, [loadInvoice]);

  useEffect(() => {
    if (activeTab === "audit" || activeTab === "approval") {
      loadAudit();
    }
  }, [activeTab, loadAudit]);

  const status = normalizeStatus(invoice?.status_code);
  const items = Array.isArray(invoice?.items) ? invoice.items : [];
  const charges = Array.isArray(invoice?.charges) ? invoice.charges : [];

  const canEdit = status === "DRAFT";
  const canSubmit = status === "DRAFT";

  const canPost =   status === "APPROVED" &&   !invoice?.journal_header_id;


  const canCancel =
    ["DRAFT", "APPROVED"].includes(status) &&
    !invoice?.journal_header_id;

  const lifecycle = useMemo(() => {
    const stages = [
      "DRAFT",
      "PENDING_APPROVAL",
      "APPROVED",
      "POSTED",
    ];

    const currentIndex = stages.indexOf(status);

    if (status === "REJECTED") {
      return { rejected: true, active: 1 };
    }

    if (status === "CANCELLED") {
      return { cancelled: true, active: -1 };
    }

    return { active: currentIndex < 0 ? 0 : currentIndex };
  }, [status]);

  const handleSubmit = async () => {
    if (!window.confirm("Submit this Sales Invoice for approval?")) {
      return;
    }

    setProcessing(true);
    setError("");
    setSuccess("");

    try {
      await tradingSalesInvoiceService.submitSalesInvoiceForApproval(id, {});

      setSuccess("Sales Invoice submitted for approval successfully.");

      await loadInvoice();
      await loadAudit();
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setProcessing(false);
    }
  };

  const handlePost = async () => {
  if (
    !window.confirm(
      "Are you sure you want to post this Sales Invoice to Finance?"
    )
  ) {
    return;
  }

  setProcessing(true);
  setError("");
  setSuccess("");

  try {
    await tradingSalesInvoiceService.postSalesInvoice(id, {});

    setSuccess(
      "Sales Invoice posted successfully to Finance."
    );

    await loadInvoice();
    await loadAudit();
  } catch (err) {
    setError(getErrorMessage(err));
  } finally {
    setProcessing(false);
  }
};
  
  const handleCancel = async () => {
    const reason = window.prompt(
      "Enter the reason for cancelling this Sales Invoice:"
    );

    if (reason === null) return;

    if (!reason.trim()) {
      setError("Cancellation reason is required.");
      return;
    }

    setProcessing(true);
    setError("");
    setSuccess("");

    try {
      await tradingSalesInvoiceService.cancelSalesInvoice(id, {
        cancellation_reason: reason.trim(),
      });

      setSuccess("Sales Invoice cancelled successfully.");

      await loadInvoice();
      await loadAudit();
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div style={styles.page}>
        <div
          style={{
            ...styles.card,
            padding: 32,
            textAlign: "center",
            color: "#667085",
          }}
        >
          Loading Sales Invoice...
        </div>
      </div>
    );
  }

  if (!invoice) {
    return (
      <div style={styles.page}>
        <div style={styles.header}>
          <div>
            <h1 style={styles.title}>Sales Invoice</h1>
            <p style={styles.subtitle}>Invoice details workspace</p>
          </div>

          <button
            style={styles.button}
            onClick={() => navigate("/trading/sales-invoices")}
          >
            ← Back to Invoice Register
          </button>
        </div>

        <div role="alert" style={styles.alert}>
          {error || "Sales Invoice not found."}
        </div>
      </div>
    );
  }

  const itemGross = Number(invoice.item_gross_amount || 0);
  const discount = Number(invoice.item_discount_amount || 0);
  const taxable = Number(invoice.taxable_amount || 0);
  const taxTotal = Number(invoice.total_tax_amount || 0);
  const chargeAmount = Number(invoice.charge_amount || 0);
  const chargeTax = Number(invoice.charge_tax_amount || 0);
  const netAmount = Number(invoice.net_amount || 0);

  const approvalEvents = audit.filter((entry) =>
    /SUBMIT|APPROV|REJECT/i.test(
      `${entry.event_type || ""} ${entry.field_name || ""}`
    )
  );

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Sales Invoice Workspace</h1>
          <p style={styles.subtitle}>
            Review invoice details, source documents, commercial values and
            workflow history.
          </p>
        </div>

        <div style={styles.buttonRow}>
          <button
            style={styles.button}
            onClick={() => navigate("/trading/sales-invoices")}
          >
            ← Invoice Register
          </button>

          <button style={styles.button} onClick={() => window.print()}>
            Print
          </button>

          {canEdit && (
            <button
              style={styles.button}
              onClick={() =>
                navigate(`/trading/sales-invoices/${id}/edit`)
              }
            >
              Edit Invoice
            </button>
          )}

          {canSubmit && (
            <button
              style={styles.primaryButton}
              disabled={processing}
              onClick={handleSubmit}
            >
              {processing ? "Processing..." : "Submit for Approval"}
            </button>
          )}
          {canPost && (
            <button
              style={styles.primaryButton}
              disabled={processing}
              onClick={handlePost}
            >
              {processing ? "Processing..." : "Post to Finance"}
            </button>
          )}  
          {canCancel && (
            <button
              style={styles.dangerButton}
              disabled={processing}
              onClick={handleCancel}
            >
              Cancel Invoice
            </button>
          )}
        </div>
      </div>

      {error && (
        <div role="alert" style={styles.alert}>
          {error}
        </div>
      )}

      {success && (
        <div
          role="status"
          style={{
            ...styles.alert,
            background: "#ecfdf3",
            borderColor: "#b7ebc6",
            color: "#167647",
          }}
        >
          {success}
        </div>
      )}

      <section style={{ ...styles.card, ...styles.hero }}>
        <div style={styles.heroTop}>
          <div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                flexWrap: "wrap",
                marginBottom: 9,
              }}
            >
              <h2 style={styles.invoiceNo}>
                {invoice.invoice_no || `SI-${invoice.id}`}
              </h2>

              <StatusBadge status={status} />
            </div>

            <div style={styles.customerName}>
              {invoice.customer_name || "Customer not available"}
            </div>

            <div style={styles.muted}>
              Customer ID: {invoice.customer_id || "—"} · Sales Order:{" "}
              {invoice.sales_order_no ||
                invoice.sales_order?.sales_order_no ||
                "—"}
            </div>
          </div>

          <div style={styles.amountBlock}>
            <div style={styles.muted}>Invoice Grand Total</div>

            <div style={styles.grandAmount}>
              {invoice.currency_code || "₹"} {formatAmount(netAmount)}
            </div>

            <div style={styles.muted}>
              Invoice Date: {formatDate(invoice.invoice_date)}
            </div>
          </div>
        </div>

        <div style={styles.metricGrid}>
          <div style={styles.metric}>
            <div style={styles.metricLabel}>Taxable Amount</div>
            <div style={styles.metricValue}>
              ₹ {formatAmount(taxable)}
            </div>
          </div>

          <div style={styles.metric}>
            <div style={styles.metricLabel}>Total GST / Tax</div>
            <div style={styles.metricValue}>
              ₹ {formatAmount(taxTotal + chargeTax)}
            </div>
          </div>

          <div style={styles.metric}>
            <div style={styles.metricLabel}>Invoice Items</div>
            <div style={styles.metricValue}>{items.length}</div>
          </div>

          <div style={styles.metric}>
            <div style={styles.metricLabel}>Due Date</div>
            <div style={styles.metricValue}>
              {formatDate(invoice.due_date)}
            </div>
          </div>
        </div>

        <div style={styles.lifecycle}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              gap: 10,
              flexWrap: "wrap",
            }}
          >
            <strong style={{ fontSize: 12, color: "#344054" }}>
              Invoice Lifecycle
            </strong>

            <span style={styles.muted}>
              {status.replaceAll("_", " ")}
            </span>
          </div>

          {lifecycle.rejected ? (
            <div
              style={{
                ...styles.note,
                background: "#fff1f0",
                color: "#b42318",
              }}
            >
              Invoice rejected.{" "}
              {invoice.rejection_reason ||
                "Review the rejection details in the workflow history."}
            </div>
          ) : lifecycle.cancelled ? (
            <div style={{ ...styles.note, background: "#f2f4f7" }}>
              This invoice has been cancelled.
            </div>
          ) : (
            <div style={styles.steps}>
              {[
                "Created",
                "Approval",
                "Approved",
                "Finance Posting",
              ].map((label, index) => (
                <div key={label}>
                  <div style={styles.stepTrack}>
                    <div
                      style={{
                        ...styles.stepFill,
                        width:
                          index <= lifecycle.active ? "100%" : "0%",
                      }}
                    />
                  </div>

                  <div
                    style={{
                      ...styles.stepLabel,
                      color:
                        index <= lifecycle.active
                          ? "#16845b"
                          : "#8992a4",
                      fontWeight:
                        index === lifecycle.active ? 800 : 500,
                    }}
                  >
                    {label}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      <div style={styles.contentGrid}>
        <section style={{ ...styles.card, ...styles.mainCard }}>
          <h3 style={styles.sectionTitle}>Invoice Details</h3>

          <div style={styles.tabs}>
            {[
              ["items", "Invoice Items"],
              ["delivery", "Source Delivery Challans"],
              ["charges", "Charges & Taxes"],
              ["approval", "Approval History"],
              ["audit", "Audit Trail"],
            ].map(([key, label]) => (
              <button
                key={key}
                style={{
                  ...styles.tab,
                  ...(activeTab === key ? styles.activeTab : {}),
                }}
                onClick={() => setActiveTab(key)}
              >
                {label}
              </button>
            ))}
          </div>

          {activeTab === "items" && (
            <>
              <div style={styles.tableWrap}>
                <table style={styles.table}>
                  <thead>
                    <tr>
                      <th style={styles.th}># / Item Description</th>
                      <th style={styles.th}>Source DC</th>
                      <th style={{ ...styles.th, ...styles.right }}>
                        Qty
                      </th>
                      <th style={{ ...styles.th, ...styles.right }}>
                        Rate
                      </th>
                      <th style={{ ...styles.th, ...styles.right }}>
                        Discount
                      </th>
                      <th style={{ ...styles.th, ...styles.right }}>
                        Taxable
                      </th>
                      <th style={{ ...styles.th, ...styles.right }}>
                        GST
                      </th>
                      <th style={{ ...styles.th, ...styles.right }}>
                        Line Total
                      </th>
                    </tr>
                  </thead>

                  <tbody>
                    {items.length ? (
                      items.map((item, index) => (
                        <tr
                          key={
                            item.id ||
                            `${item.delivery_challan_item_id}-${index}`
                          }
                        >
                          <td style={styles.td}>
                            <strong>
                              {item.item_description ||
                                `Item ${item.item_seq_no || index + 1}`}
                            </strong>

                            <div style={styles.muted}>
                              Item ID:{" "}
                              {item.raw_material_id ||
                                item.sales_order_item_id ||
                                "—"}
                            </div>
                          </td>

                          <td style={styles.td}>
                            {item.delivery_challan_no ||
                              `DC #${item.delivery_challan_id || "—"}`}

                            <div style={styles.muted}>
                              {formatDate(item.delivery_challan_date)}
                            </div>
                          </td>

                          <td style={{ ...styles.td, ...styles.right }}>
                            {formatQuantity(item.invoice_quantity)}
                          </td>

                          <td style={{ ...styles.td, ...styles.right }}>
                            {formatAmount(item.unit_selling_price)}
                          </td>

                          <td style={{ ...styles.td, ...styles.right }}>
                            {formatAmount(item.discount_amount)}

                            <div style={styles.muted}>
                              {formatQuantity(item.discount_percent)}%
                            </div>
                          </td>

                          <td style={{ ...styles.td, ...styles.right }}>
                            {formatAmount(item.taxable_amount)}
                          </td>

                          <td style={{ ...styles.td, ...styles.right }}>
                            {formatAmount(item.tax_amount)}
                          </td>

                          <td
                            style={{
                              ...styles.td,
                              ...styles.right,
                              fontWeight: 800,
                            }}
                          >
                            {formatAmount(item.line_total_amount)}
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={8} style={styles.empty}>
                          No invoice items found.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              <div style={styles.infoGrid}>
                <Info
                  label="Customer Reference"
                  value={invoice.customer_reference}
                />

                <Info
                  label="Payment Terms"
                  value={invoice.payment_terms}
                />

                <Info
                  label="Currency"
                  value={invoice.currency_code}
                />

                <Info
                  label="Exchange Rate"
                  value={invoice.exchange_rate}
                />
              </div>
            </>
          )}

          {activeTab === "delivery" && (
            <>
              <div style={styles.note}>
                These Delivery Challans are the source documents used to
                create this invoice. One invoice may include multiple
                challans.
              </div>

              <div style={styles.tableWrap}>
                <table style={styles.table}>
                  <thead>
                    <tr>
                      <th style={styles.th}>Delivery Challan</th>
                      <th style={styles.th}>DC Date</th>
                      <th style={styles.th}>Invoice Item</th>
                      <th style={{ ...styles.th, ...styles.right }}>
                        Invoiced Qty
                      </th>
                    </tr>
                  </thead>

                  <tbody>
                    {items.length ? (
                      items.map((item, index) => (
                        <tr key={item.id || index}>
                          <td style={styles.td}>
                            <strong>
                              {item.delivery_challan_no ||
                                `DC #${item.delivery_challan_id || "—"}`}
                            </strong>
                          </td>

                          <td style={styles.td}>
                            {formatDate(item.delivery_challan_date)}
                          </td>

                          <td style={styles.td}>
                            {item.item_description || "—"}
                          </td>

                          <td
                            style={{ ...styles.td, ...styles.right }}
                          >
                            {formatQuantity(item.invoice_quantity)}
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={4} style={styles.empty}>
                          No source Delivery Challans found.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              <div style={styles.infoGrid}>
                <Info
                  label="Sales Order Number"
                  value={
                    invoice.sales_order?.sales_order_no ||
                    invoice.sales_order_no
                  }
                />

                <Info
                  label="Sales Order Date"
                  value={formatDate(
                    invoice.sales_order?.sales_order_date
                  )}
                />

                <Info
                  label="Sales Order Status"
                  value={invoice.sales_order?.status_code}
                />
              </div>
            </>
          )}

          {activeTab === "charges" && (
            <>
              <div style={styles.tableWrap}>
                <table style={styles.table}>
                  <thead>
                    <tr>
                      <th style={styles.th}>Charge Type</th>
                      <th style={styles.th}>Description</th>
                      <th style={{ ...styles.th, ...styles.right }}>
                        Base Amount
                      </th>
                      <th style={{ ...styles.th, ...styles.right }}>
                        GST Rate
                      </th>
                      <th style={{ ...styles.th, ...styles.right }}>
                        CGST
                      </th>
                      <th style={{ ...styles.th, ...styles.right }}>
                        SGST
                      </th>
                      <th style={{ ...styles.th, ...styles.right }}>
                        IGST
                      </th>
                      <th style={{ ...styles.th, ...styles.right }}>
                        Total GST
                      </th>
                    </tr>
                  </thead>

                  <tbody>
                    {charges.length ? (
                      charges.map((charge, index) => (
                        <tr key={charge.id || index}>
                          <td style={styles.td}>
                            <strong>{charge.charge_type}</strong>
                          </td>

                          <td style={styles.td}>
                            {charge.description ||
                              charge.remarks ||
                              "—"}
                          </td>

                          <td
                            style={{ ...styles.td, ...styles.right }}
                          >
                            {formatAmount(charge.charge_amount)}
                          </td>

                          <td
                            style={{ ...styles.td, ...styles.right }}
                          >
                            {formatQuantity(charge.gst_rate)}%
                          </td>

                          <td
                            style={{ ...styles.td, ...styles.right }}
                          >
                            {formatAmount(charge.cgst_amount)}
                          </td>

                          <td
                            style={{ ...styles.td, ...styles.right }}
                          >
                            {formatAmount(charge.sgst_amount)}
                          </td>

                          <td
                            style={{ ...styles.td, ...styles.right }}
                          >
                            {formatAmount(charge.igst_amount)}
                          </td>

                          <td
                            style={{ ...styles.td, ...styles.right }}
                          >
                            {formatAmount(charge.gst_amount)}
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={8} style={styles.empty}>
                          No additional charges on this invoice.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              <div style={styles.infoGrid}>
                <Info
                  label="Invoice Taxable Amount"
                  value={`₹ ${formatAmount(taxable)}`}
                />

                <Info
                  label="CGST"
                  value={`₹ ${formatAmount(invoice.cgst_amount)}`}
                />

                <Info
                  label="SGST"
                  value={`₹ ${formatAmount(invoice.sgst_amount)}`}
                />

                <Info
                  label="IGST"
                  value={`₹ ${formatAmount(invoice.igst_amount)}`}
                />

                <Info
                  label="Other Tax"
                  value={`₹ ${formatAmount(invoice.other_tax_amount)}`}
                />
              </div>
            </>
          )}

          {activeTab === "approval" && (
            <div style={{ marginTop: 12 }}>
              {auditLoading ? (
                <div style={styles.empty}>
                  Loading approval history...
                </div>
              ) : approvalEvents.length ? (
                approvalEvents.map((entry) => (
                  <div key={entry.id} style={styles.timelineItem}>
                    <div style={styles.dot} />

                    <div>
                      <strong
                        style={{ fontSize: 12, color: "#344054" }}
                      >
                        {String(
                          entry.event_type || "Workflow Event"
                        ).replaceAll("_", " ")}
                      </strong>

                      <div style={styles.muted}>
                        {entry.remarks ||
                          entry.reason ||
                          entry.field_name ||
                          "—"}
                      </div>

                      <div style={styles.muted}>
                        {formatDateTime(entry.performed_at)} · User ID:{" "}
                        {entry.performed_by || "—"}
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div style={styles.empty}>
                  No approval events recorded yet.
                </div>
              )}

              {status === "PENDING_APPROVAL" && (
                <div style={styles.note}>
                  Approval and rejection decisions are handled through
                  the existing Approval Matrix workflow. This screen
                  displays the recorded invoice events.
                </div>
              )}
            </div>
          )}

          {activeTab === "audit" && (
            <div style={{ marginTop: 12 }}>
              {auditLoading ? (
                <div style={styles.empty}>Loading audit history...</div>
              ) : audit.length ? (
                audit.map((entry) => (
                  <div key={entry.id} style={styles.timelineItem}>
                    <div style={styles.dot} />

                    <div>
                      <strong
                        style={{ fontSize: 12, color: "#344054" }}
                      >
                        {String(
                          entry.event_type || "Activity"
                        ).replaceAll("_", " ")}
                      </strong>

                      <div style={styles.muted}>
                        {entry.field_name
                          ? `${entry.field_name}: ${
                              entry.old_value ?? "—"
                            } → ${entry.new_value ?? "—"}`
                          : entry.remarks || "—"}
                      </div>

                      {entry.reason && (
                        <div style={styles.muted}>
                          Reason: {entry.reason}
                        </div>
                      )}

                      <div style={styles.muted}>
                        {formatDateTime(entry.performed_at)} · User ID:{" "}
                        {entry.performed_by || "—"}
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div style={styles.empty}>
                  No audit history found.
                </div>
              )}
            </div>
          )}
        </section>

        <aside style={{ ...styles.card, ...styles.sideCard }}>
          <h3 style={styles.sectionTitle}>Commercial Summary</h3>

          <div style={{ marginTop: 12 }}>
            <div style={styles.summaryRow}>
              <span>Item Gross Amount</span>
              <strong style={styles.summaryStrong}>
                ₹ {formatAmount(itemGross)}
              </strong>
            </div>

            <div style={styles.summaryRow}>
              <span>Item Discount</span>
              <strong style={styles.summaryStrong}>
                ₹ {formatAmount(discount)}
              </strong>
            </div>

            <div style={styles.summaryRow}>
              <span>Taxable Amount</span>
              <strong style={styles.summaryStrong}>
                ₹ {formatAmount(taxable)}
              </strong>
            </div>

            <div style={styles.summaryRow}>
              <span>CGST</span>
              <strong style={styles.summaryStrong}>
                ₹ {formatAmount(invoice.cgst_amount)}
              </strong>
            </div>

            <div style={styles.summaryRow}>
              <span>SGST</span>
              <strong style={styles.summaryStrong}>
                ₹ {formatAmount(invoice.sgst_amount)}
              </strong>
            </div>

            <div style={styles.summaryRow}>
              <span>IGST</span>
              <strong style={styles.summaryStrong}>
                ₹ {formatAmount(invoice.igst_amount)}
              </strong>
            </div>

            <div style={styles.summaryRow}>
              <span>Other Tax</span>
              <strong style={styles.summaryStrong}>
                ₹ {formatAmount(invoice.other_tax_amount)}
              </strong>
            </div>

            <div style={styles.summaryRow}>
              <span>Additional Charges</span>
              <strong style={styles.summaryStrong}>
                ₹ {formatAmount(chargeAmount)}
              </strong>
            </div>

            <div style={styles.summaryRow}>
              <span>Charge Tax</span>
              <strong style={styles.summaryStrong}>
                ₹ {formatAmount(chargeTax)}
              </strong>
            </div>

            <div style={styles.totalRow}>
              <strong style={{ color: "#25314a", fontSize: 13 }}>
                Grand Total
              </strong>

              <strong style={{ color: "#16845b", fontSize: 19 }}>
                ₹ {formatAmount(netAmount)}
              </strong>
            </div>
          </div>

          <div style={styles.note}>
            Amounts are displayed from the saved invoice values returned
            by the backend. The frontend does not recalculate invoice
            totals.
          </div>

          <div
            style={{
              marginTop: 17,
              paddingTop: 15,
              borderTop: "1px solid #edf0f5",
            }}
          >
            <h3 style={styles.sectionTitle}>Invoice & Customer</h3>

            <div
              style={{
                display: "grid",
                gap: 12,
                marginTop: 12,
              }}
            >
              <Info
                label="Invoice Date"
                value={formatDate(invoice.invoice_date)}
              />

              <Info
                label="Due Date"
                value={formatDate(invoice.due_date)}
              />

              <Info
                label="Customer Reference"
                value={invoice.customer_reference}
              />

              <Info
                label="Billing Address"
                value={invoice.billing_address}
              />

              <Info
                label="Shipping Address"
                value={invoice.shipping_address}
              />

              <Info label="Remarks" value={invoice.remarks} />
            </div>
          </div>

          
        </aside>
      </div>
    </div>
  );
}